/**
 * Rhythmz — App controller v3
 * ───────────────────────────
 * Multi-panel UI: presets, AI director, music composition.
 * Orchestrates WebGPU engine, audio sources, UI panels, and the self-contained
 * AI director (WebLLM + Tone.js music engine).
 */

import { VisualizerEngine } from '@engine/VisualizerEngine';
import { AudioSourceManager, type SourceKind } from '@audio/AudioSourceManager';
import { DEMO_TRACKS } from '@audio/demos';
import { CURATED_PRESETS, repairPreset, type RhythmzPreset } from '@schemas/preset';
import { ALL_PRESETS } from '@presets/catalog';
import { el, toast } from '@ui/dom';
import { aiClient } from '@ai/client';
import { SettingsPanel } from '@ai/SettingsPanel';
import { ComposerEngine } from '@ai/ComposerEngine';
import { AIAgentCoordinator } from '@ai/AIAgentCoordinator';
import { AgentPanel } from '@ai/AgentPanel';
import { SceneStore } from '@ai/SceneStore';
import { BlankCanvasAI } from '@ai/BlankCanvas';
import { DJAgent } from '@ai/DJAgent';
import * as Tone from 'tone';
import { AIConsole } from '@ai/AIConsole';
import { TelemetryDashboard } from '@ai/TelemetryDashboard';
import { ControlMatrix } from '@ai/ControlMatrix';
import { ComputePipeline } from '@engine/ComputePipeline';

export class RhythmzApp {
  private audio: AudioSourceManager;
  private engine: VisualizerEngine;
  private volume = 0.8;
  private adminToken: string | null = null;
  private mode: 'generative' | 'classic' = 'generative';
  private aiConsole: AIConsole;
  private telemetry: TelemetryDashboard;
  private controlMatrix: ControlMatrix;
  private computePipeline: ComputePipeline;
  private composerEngine: ComposerEngine | null = null;
  private agentCoordinator: AIAgentCoordinator | null = null;
  private agentPanel: AgentPanel | null = null;
  private sceneStore: SceneStore | null = null;
  private blankCanvas: BlankCanvasAI | null = null;
  private djAgent: DJAgent | null = null;
  private appMode: 'user' | 'ai' | 'dj' = 'user';
  private telemetryInterval: number | null = null;
  private _cmSubscribers: Array<(param: import('@ai/ControlMatrix').ControlParam) => void> = [];

  constructor(canvas: HTMLCanvasElement) {
    this.audio = new AudioSourceManager();
    this.engine = new VisualizerEngine(canvas, this.audio);
    this.aiConsole = new AIConsole();
    this.telemetry = new TelemetryDashboard();
    this.controlMatrix = new ControlMatrix();
    this.controlMatrix.onChange = (param) => {
      console.log(`[Glass Box] ${param.label} → ${param.isOverridden ? 'MANUAL' : 'AI'} (${param.activeValue.toFixed(3)})`);
      this.telemetry.setOverride(param.id, param.userValue ?? param.aiValue, param.aiValue);
      // Apply ControlMatrix value to the engine based on param category
      const v = param.activeValue;
      switch (param.id) {
        case 'scene.intensity': this.engine.setIntensity(v); break;
        case 'scene.speed': this.engine.setSpeed(v); break;
        case 'scene.transition.speed': this.engine.setSpeed(v); break;
        case 'particles.count': this.engine.setComplexity(v); break;
        case 'scene.brightness': this.engine.setIntensity(v); break;
        case 'scene.contrast': this.engine.setIntensity(v); break;
        default: break;
      }
      // Notify slider update listeners
      for (const fn of this._cmSubscribers) fn(param);
    };
    this.computePipeline = new ComputePipeline(16384);
    this.audio.setVolume(this.volume);
    this.audio.setStateListener((s) => this.onSourceState(s));
    this.engine.setStatsListener((s) => this.onStats(s));
  }

  async init(): Promise<void> {
    this.engine.loadPresets(CURATED_PRESETS.map((p) => ({ name: p.name, data: p })));
    await this.engine.start();

    // ── Init ComposerEngine ──
    this.composerEngine = new ComposerEngine(Tone);

    // ── Init AI Agent Coordinator + Panel ──
    const agentContainer = document.getElementById('agent-dock');
    if (agentContainer) {
      this.agentPanel = new AgentPanel(agentContainer);
      this.agentPanel.setCommandHandler((cmd, arg) => {
        this.agentCoordinator?.handleCommand(cmd, arg).then((result) => {
          if (result) toast(result.slice(0, 80));
        });
      });
      this.agentCoordinator = new AIAgentCoordinator(Tone, {
        onAgentUpdate: (name, state) => {
          this.agentPanel?.updateAgent(name, state);
          this.updateAgentChip(name, state.status);
          this.updateAIOverlay(name, state);
        },
        onThought: (agent, thought) => {
          this.agentPanel?.addThought(agent, thought);
          this.showAIThought(agent, thought);
        },
        onAction: (agent, action, target, confidence) => {
          this.agentPanel?.addLog(agent, `${action} → ${target} (${Math.round(confidence * 100)}%)`);
          this.showAIAction(agent, action, target);
        },
        onLog: (agent, msg) => this.agentPanel?.addLog(agent, msg),
      });
      this.agentCoordinator.setEngine(this.engine);
      if (this.composerEngine) {
        this.agentCoordinator.setComposerEngine(this.composerEngine);
        // Connect composer output to the visualizer's audio input so AI music drives visuals
        try { this.composerEngine.connectTo(this.audio.output); } catch { /* cross-context fallback */ }
      }
      this.agentCoordinator.start();
    }

    // ── Init Scene Store ──
    this.sceneStore = new SceneStore();
    this.renderSavedScenes();

    // ── Init Blank Canvas AI ──
    if (this.agentCoordinator) {
      this.blankCanvas = new BlankCanvasAI(this.engine, this.agentCoordinator, {
        onProgress: (step, detail) => {
          const el = document.getElementById('blank-canvas-progress');
          if (el) el.textContent = `${step}: ${detail.slice(0, 80)}`;
        },
        onComplete: (result) => {
          toast(`✨ Scene built: ${result.sceneName}`);
        },
        onError: (err) => {
          toast(`⚠️ Blank canvas error: ${err}`);
        },
        onSceneLoaded: (_sceneName, sceneIdx) => {
          // Highlight the scene card in the right dock
          document.querySelectorAll('#scene-grid .scene-card').forEach((c) => c.classList.remove('active'));
          const card = document.querySelector(`#scene-grid .scene-card[data-scene-idx="${sceneIdx}"]`);
          if (card) card.classList.add('active');
        },
      });
    }

    // ── Init DJ Agent ──
    if (this.agentCoordinator && this.composerEngine) {
      this.djAgent = new DJAgent(this.agentCoordinator, {
        onTrackChange: (track, idx) => {
          this.updateDJDisplay(idx, track);
        },
        onTransition: (type, from, to) => {
          toast(`🎚 ${type}: ${from.name} → ${to.name}`);
        },
        onProgress: (msg) => {
          const el = document.getElementById('dj-progress');
          if (el) el.textContent = msg.slice(0, 100);
        },
        onSetlistGenerated: (setlist) => {
          this.renderDJSetlist(setlist);
        },
      });
      this.djAgent.setComposer(this.composerEngine);
    }

    this.bindUI();
    this.bindModeToggle();
    this.bindBlankCanvas();
    this.bindDJ();
    this.bindSavedScenes();
    this.renderSceneGrid();
    this.renderPresetRail();
    this.checkAdminAccess();

    const adminPresets = document.querySelector('#admin-presets');
    if (adminPresets) adminPresets.textContent = String(CURATED_PRESETS.length);

    (window as unknown as { __rhythmz: unknown }).__rhythmz = this;

    // NOTE: SceneDirector (WebLLM) removed — AIAgentCoordinator replaces it entirely.
    // The old dual-AI system caused conflicts. The coordinator's 3 agents handle everything.

    this.startTelemetryStream();
    this.wireComputeEffects();

    // Frame loop — AI updates gated by mode
    const tick = (): void => {
      // Only run AI loops in AI or DJ mode (not in user mode)
      if (this.appMode !== 'user' && this.agentCoordinator) {
        const af = this.engine.getAudioFeatures();
        this.agentCoordinator.update(af);
      }
      this.updateMusicUI();
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  /** Expose toolkit + mutator + music engine on window for debugging (admin only). */
  private exposeToolkit(): void {
    const w = window as unknown as Record<string, unknown>;
    if (this.agentCoordinator) {
      w.__coordinator = this.agentCoordinator;
      w.__music = this.agentCoordinator.musicAI;
      w.__visualDJ = this.agentCoordinator.visualDJ;
      w.__research = this.agentCoordinator.research;
    }
    if (this.composerEngine) w.__composer = this.composerEngine;
    if (this.djAgent) w.__djAgent = this.djAgent;
    import('./ai/SceneToolkit').then((tk) => { w.__toolkit = tk; });
  }

  loadAIPreset(preset: unknown): void {
    const repaired = repairPreset(preset);
    this.engine.addPreset(repaired.name || 'AI Generated', repaired);
  }

  /* ─────────────── UI Wiring ─────────────── */

  private bindUI(): void {
    // Source buttons
    el('#src-demo').onclick = () => this.pickDemo();
    el('#src-file').onclick = () => this.pickFile();
    el('#src-mic').onclick = () => this.useMic();
    el('#src-desktop').onclick = () => this.useDesktop();
    el('#src-url').onclick = () => this.useUrl();
    el('#src-video').onclick = () => this.loadVideo();

    // Preset navigation
    el('#preset-prev').onclick = () => this.engine.prevPreset();
    el('#preset-next').onclick = () => this.engine.nextPreset();
    el('#preset-shuffle').onclick = () => this.engine.loadRandom();
    el<HTMLInputElement>('#preset-search').oninput = (e) =>
      this.searchPresets((e.target as HTMLInputElement).value);

    // Volume
    el<HTMLInputElement>('#volume').oninput = (e) => {
      this.volume = parseFloat((e.target as HTMLInputElement).value);
      this.audio.setVolume(this.volume);
    };

    // Auto-rotate
    const autoRotate = el<HTMLInputElement>('#auto-rotate');
    autoRotate.onchange = () => {
      this.engine.setAutoRotate(autoRotate.checked);
    };

    // Fullscreen
    el('#fullscreen').onclick = () => this.toggleFullscreen();

    // AI Settings button
    el('#ai-settings-btn').onclick = () => this.showSettings();

    // Hide UI toggle
    el('#hide-ui').onclick = () => this.toggleHideUI();

    // Effect buttons
    document.querySelectorAll('.effect-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const effect = (btn as HTMLElement).dataset.effect;
        if (effect) {
          this.agentCoordinator?.visualDJ.applySceneEffect(effect);
          toast(`✨ ${effect} applied`);
        }
      });
    });

    // Parameter sliders → engine (ALL 8 engine uniforms now controllable)
    this.wireParamSlider('#param-speed', '#param-speed-val', (v) => this.engine.setSpeed(v));
    this.wireParamSlider('#param-intensity', '#param-intensity-val', (v) => this.engine.setIntensity(v));
    this.wireParamSlider('#param-warp', '#param-warp-val', (v) => this.engine.setWarp(v));
    this.wireParamSlider('#param-complexity', '#param-complexity-val', (v) => this.engine.setComplexity(v));
    this.wireParamSlider('#param-scale', '#param-scale-val', (v) => this.engine.setScale(v));
    this.wireParamSlider('#param-rotation', '#param-rotation-val', (v) => this.engine.setRotation(v));
    this.wireParamSlider('#param-hue', '#param-hue-val', (v) => this.engine.setHueShift(v));
    this.wireParamSlider('#param-bloom', '#param-bloom-val', (v) => this.engine.setBloomStrength(v));

    // Palette color pickers → engine
    ['#color-0', '#color-1', '#color-2'].forEach((sel, idx) => {
      const picker = document.querySelector<HTMLInputElement>(sel);
      if (!picker) return;
      picker.oninput = () => {
        const hex = picker.value;
        const r = parseInt(hex.slice(1, 3), 16) / 255;
        const g = parseInt(hex.slice(3, 5), 16) / 255;
        const b = parseInt(hex.slice(5, 7), 16) / 255;
        this.engine.setPaletteColor(idx, r, g, b);
      };
    });

    // AI prompt generation
    el('#ai-generate').onclick = () => this.generateFromPrompt(el<HTMLInputElement>('#ai-prompt').value);
    el<HTMLInputElement>('#ai-prompt').addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this.generateFromPrompt((e.target as HTMLInputElement).value);
    });

    // Welcome
    el('#enter').onclick = async () => {
      await this.audio.resume();
      el('#welcome').classList.add('hidden');
    };

    // Keyboard
    window.addEventListener('keydown', (e) => this.onKey(e));

    // Init settings panel
    this.initSettingsPanel();

    // Attach compute pipeline
    setTimeout(() => {
      try { this.computePipeline.attachRenderer(this.engine as any); } catch {}
    }, 1000);

    // Mobile drawer toggles
    this.bindDrawers();
  }

  /** Wire mobile drawer toggle buttons. */
  private bindDrawers(): void {
    const leftToggle = document.getElementById('toggle-left-drawer');
    const rightToggle = document.getElementById('toggle-right-drawer');
    const leftDock = document.querySelector('.dock-left');
    const rightDock = document.querySelector('.dock-right');

    leftToggle?.addEventListener('click', (e) => {
      e.stopPropagation();
      leftDock?.classList.toggle('open');
      rightDock?.classList.remove('open');
    });
    rightToggle?.addEventListener('click', (e) => {
      e.stopPropagation();
      rightDock?.classList.toggle('open');
      leftDock?.classList.remove('open');
    });

    // Close drawers when clicking on the canvas (but not on the toggle buttons)
    document.getElementById('viz')?.addEventListener('click', () => {
      if (leftDock?.classList.contains('open') || rightDock?.classList.contains('open')) {
        leftDock?.classList.remove('open');
        rightDock?.classList.remove('open');
      }
    });
  }

  /** Wire a parameter slider to the engine + update the value label. */
  private wireParamSlider(sliderSel: string, valSel: string, fn: (v: number) => void): void {
    const slider = document.querySelector<HTMLInputElement>(sliderSel);
    const val = document.querySelector<HTMLElement>(valSel);
    if (!slider || !val) return;
    slider.oninput = () => {
      const v = parseFloat(slider.value);
      fn(v);
      val.textContent = v.toFixed(2);
    };
  }

  /** Update the agent status chip in the top bar. */
  private updateAgentChip(name: string, status: string): void {
    const chipMap: Record<string, string> = {
      'Music AI': 'chip-music-status',
      'Visual DJ AI': 'chip-visual-status',
      'Research AI': 'chip-research-status',
    };
    const chipId = chipMap[name];
    if (!chipId) return;
    const chip = document.getElementById(chipId);
    const chipEl = document.getElementById(chipId.replace('-status', ''));
    if (chip) chip.textContent = status;
    if (chipEl) {
      chipEl.classList.remove('thinking', 'working', 'done', 'error');
      if (status !== 'idle') chipEl.classList.add(status);
    }
  }

  /** Update the AI overlay on the canvas — visible proof of AI working. */
  private updateAIOverlay(name: string, state: { status: string; lastAction: string }): void {
    const overlay = document.getElementById('ai-overlay');
    const text = document.getElementById('ai-overlay-text');
    if (!overlay || !text) return;

    if (state.status === 'thinking' || state.status === 'working') {
      overlay.classList.add('active');
      const icon = state.status === 'thinking' ? '🧠' : '⚡';
      text.textContent = `${icon} ${name}: ${state.lastAction.slice(0, 60)}`;
    } else if (state.status === 'done') {
      overlay.classList.add('active');
      text.textContent = `✅ ${name}: ${state.lastAction.slice(0, 60)}`;
      // Auto-dim after 2s
      setTimeout(() => {
        if (overlay.classList.contains('active') && text.textContent?.startsWith('✅')) {
          overlay.classList.remove('active');
          text.textContent = 'AI agents monitoring';
        }
      }, 2000);
    } else if (state.status === 'error') {
      overlay.classList.add('active');
      text.textContent = `❌ ${name}: ${state.lastAction.slice(0, 60)}`;
    }
  }

  /** Show AI thought in the overlay briefly. */
  private showAIThought(agent: string, thought: string): void {
    const text = document.getElementById('ai-overlay-text');
    if (!text) return;
    const shortAgent = agent.replace(' AI', '');
    text.textContent = `🧠 ${shortAgent}: ${thought.slice(0, 50)}`;
  }

  /** Show AI action in the scene indicator. */
  private showAIAction(_agent: string, action: string, target: string): void {
    const indicator = document.getElementById('ai-scene-indicator');
    const nameEl = document.getElementById('ai-scene-name');
    if (!indicator || !nameEl) return;

    if (action.includes('scene') || action.includes('Scene')) {
      indicator.style.display = 'block';
      nameEl.textContent = target;
    }
  }

  /** Render the 15 scene cards in the right dock. */
  private renderSceneGrid(): void {
    const grid = document.getElementById('scene-grid');
    if (!grid) return;
    const scenes = [
      'CrystallineSpire', 'KaleidoscopicTorus', 'ParticleNebula', 'FractalTunnel',
      'GeometricBloom', 'LightBeams', 'PlasmaGrid', 'TheVoidV2', 'BasiliskSkull',
      'CrystalColossus', 'MechanicalBeast', 'FractalDragon', 'BioluminescentForest',
      'Cuttlefish', 'GeometricMandala',
    ];
    grid.innerHTML = scenes.map((s, i) =>
      `<button class="scene-card" data-scene-idx="${i}">${s.replace(/([A-Z])/g, ' $1').trim()}</button>`
    ).join('');
    grid.querySelectorAll('.scene-card').forEach((card) => {
      card.addEventListener('click', () => {
        const idx = parseInt((card as HTMLElement).dataset.sceneIdx ?? '0', 10);
        const mgr = this.engine.sceneManagerAccess;
        if (mgr) {
          mgr.loadScene(idx);
          grid.querySelectorAll('.scene-card').forEach((c) => c.classList.remove('active'));
          card.classList.add('active');
          toast(`🎬 ${scenes[idx]}`);
        }
      });
    });
  }

  /* ─────────────── Mode System ─────────────── */

  private bindModeToggle(): void {
    document.querySelectorAll('.mode-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const mode = (btn as HTMLElement).dataset.mode as 'user' | 'ai' | 'dj';
        this.setMode(mode);
      });
    });
  }

  private setMode(mode: 'user' | 'ai' | 'dj'): void {
    this.appMode = mode;
    document.querySelectorAll('.mode-btn').forEach((b) => b.classList.remove('active'));
    document.querySelector(`.mode-btn[data-mode="${mode}"]`)?.classList.add('active');

    const bcPanel = document.getElementById('blank-canvas-panel');
    const djPanel = document.getElementById('dj-panel');
    if (bcPanel) bcPanel.style.display = mode === 'ai' ? 'block' : 'none';
    if (djPanel) djPanel.style.display = mode === 'dj' ? 'block' : 'none';

    if (mode === 'user') {
      this.engine.setAudioReactive(true);
      this.engine.setAutoRotate(true);
      this.djAgent?.stop();
      this.blankCanvas?.stopWordCycle();
      toast('👤 User mode — manual control');
    } else if (mode === 'ai') {
      this.engine.setAutoRotate(false);
      this.blankCanvas?.startWordCycle();
      this.djAgent?.stop();
      toast('🎨 AI mode — describe a scene and the AI builds it');
    } else if (mode === 'dj') {
      this.engine.setAudioReactive(true);
      this.blankCanvas?.stopWordCycle();
      toast('🎧 DJ mode — generate a setlist and let the AI mix');
    }
  }

  /* ─────────────── Blank Canvas ─────────────── */

  private bindBlankCanvas(): void {
    const genBtn = document.getElementById('blank-canvas-generate');
    genBtn?.addEventListener('click', () => this.triggerBlankCanvas());

    ['mood', 'color', 'scene', 'genre'].forEach((pool) => {
      const btn = document.getElementById(`word-${pool}`);
      btn?.addEventListener('click', () => {
        if (!this.blankCanvas) return;
        if (btn.classList.contains('locked')) {
          this.blankCanvas.unlockWord(pool);
          btn.classList.remove('locked');
        } else {
          this.blankCanvas.lockWord(pool);
          btn.classList.add('locked');
        }
      });
    });
  }

  private async triggerBlankCanvas(): Promise<void> {
    const descEl = document.getElementById('blank-canvas-desc') as HTMLTextAreaElement | null;
    let desc = descEl?.value?.trim();
    if (!desc) {
      desc = this.blankCanvas?.buildDescriptionFromWords() ?? 'dark cosmic scene';
    }
    toast(`🤖 AI building: "${desc.slice(0, 40)}..."`);
    await this.blankCanvas?.generate(desc);
  }

  /* ─────────────── AI DJ ─────────────── */

  private bindDJ(): void {
    document.getElementById('dj-generate')?.addEventListener('click', async () => {
      const vibeEl = document.getElementById('dj-vibe') as HTMLInputElement | null;
      const vibe = vibeEl?.value?.trim() || 'dark techno sunrise set';
      toast('🎧 AI DJ generating setlist...');
      await this.djAgent?.generateSetlist(vibe);
    });

    document.getElementById('dj-play')?.addEventListener('click', async () => {
      await this.audio.resume(); // Ensure AudioContext is running (user gesture)
      this.djAgent?.play();
      toast('▶ DJ setlist playing');
    });

    document.getElementById('dj-stop')?.addEventListener('click', () => {
      this.djAgent?.stop();
      toast('⏹ DJ stopped');
    });

    document.getElementById('dj-skip')?.addEventListener('click', () => {
      this.djAgent?.skip();
      toast('⏭ Skipped to next track');
    });
  }

  private renderDJSetlist(setlist: { tracks: Array<{ name: string; genre: string; bpm: number; transition: string; energy: number }>; totalTracks: number; estimatedMinutes: number }): void {
    const el = document.getElementById('dj-setlist');
    if (!el) return;
    el.innerHTML = setlist.tracks.map((t, i) => {
      const energyBar = '█'.repeat(Math.round(t.energy * 5)) + '░'.repeat(5 - Math.round(t.energy * 5));
      const transIcon = t.transition === 'cut' ? '✂️' : t.transition === 'mix' ? '🎚' : '🌫';
      return `<div style="padding:3px 4px;border-bottom:1px solid rgba(255,255,255,0.04);">
        <span style="color:#6366f1;">${i + 1}.</span> ${t.name} · ${transIcon} ${t.genre} ${t.bpm}bpm
        <span style="opacity:0.5;">${energyBar}</span>
      </div>`;
    }).join('');
  }

  private updateDJDisplay(idx: number, track: { name: string; genre: string; bpm: number }): void {
    const el = document.getElementById('dj-progress');
    if (el) el.textContent = `▶ [${idx + 1}] ${track.name} — ${track.genre} ${track.bpm}bpm`;
  }

  /* ─────────────── Saved Scenes ─────────────── */

  private bindSavedScenes(): void {
    document.getElementById('save-scene-btn')?.addEventListener('click', () => this.saveCurrentScene());
  }

  private saveCurrentScene(): void {
    if (!this.sceneStore) return;
    const state = this.engine.serializeState();
    const name = (state.name as string) ?? 'Untitled';
    this.sceneStore.saveScene(name, state);
    toast(`💾 Saved: ${name}`);
    this.renderSavedScenes();
  }

  private renderSavedScenes(): void {
    const list = document.getElementById('saved-scenes-list');
    if (!list || !this.sceneStore) return;
    const scenes = this.sceneStore.list();
    if (scenes.length === 0) {
      list.innerHTML = '<div style="opacity:0.4;text-align:center;padding:4px;">No saved scenes yet</div>';
      return;
    }
    list.innerHTML = scenes.map((s) => `
      <div class="saved-scene-item">
        <span class="saved-scene-name">${s.name}</span>
        <button class="saved-scene-btn" data-action="load" data-id="${s.id}" title="Load">▶</button>
        <button class="saved-scene-btn del" data-action="delete" data-id="${s.id}" title="Delete">✕</button>
      </div>
    `).join('');

    list.querySelectorAll('.saved-scene-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const action = (btn as HTMLElement).dataset.action;
        const id = (btn as HTMLElement).dataset.id;
        if (!id || !this.sceneStore) return;
        if (action === 'load') {
          const scene = this.sceneStore.get(id);
          if (scene) {
            this.engine.addPreset(scene.name, scene.state as Record<string, unknown>);
            toast(`▶ Loaded: ${scene.name}`);
          }
        } else if (action === 'delete') {
          this.sceneStore.delete(id);
          this.renderSavedScenes();
          toast('🗑 Deleted');
        }
      });
    });
  }

  /** Initialize the AI settings panel. */
  private settingsPanel: SettingsPanel | null = null;
  private initSettingsPanel(): void {
    const container = document.getElementById('settings-container');
    if (container) {
      this.settingsPanel = new SettingsPanel(container);
      this.settingsPanel.render();
      this.settingsPanel.onChange(() => toast('⚙ AI settings updated'));
    }
  }

  /** Show the settings overlay. */
  private showSettings(): void {
    this.settingsPanel?.show();
  }

  /** Poll music engine for status updates in the UI. */
  private updateMusicUI(): void {
    const label = document.getElementById('source-label');
    if (label && this.audio.element) {
      label.textContent = this.audio.element.paused ? 'Paused' : 'Playing';
    }
  }

  /** Toggle hide-all UI mode. */
  private toggleHideUI(): void {
    document.body.classList.toggle('hide-ui');
    const btn = document.getElementById('hide-ui');
    if (btn) btn.textContent = document.body.classList.contains('hide-ui') ? '👁‍🗨' : '👁';
  }

  /* ─────────────── Keyboard ─────────────── */

  private onKey(e: KeyboardEvent): void {
    const tag = (e.target as HTMLElement)?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
    switch (e.key) {
      case 'ArrowRight': this.engine.nextPreset(); break;
      case 'ArrowLeft': this.engine.prevPreset(); break;
      case 'r': case 'R': this.engine.loadRandom(); break;
      case 'f': case 'F': this.toggleFullscreen(); break;
      case 'm': case 'M': this.useMic(); break;
      case 'H': this.toggleHideUI(); break;
      case 't': case 'T': if (e.ctrlKey) { e.preventDefault(); this.telemetry.toggle(); } break;
      case ' ': if (e.ctrlKey) { e.preventDefault(); this.aiConsole.toggle(); }
                else { e.preventDefault(); this.audio.togglePlay(); } break;
    }
  }

  /* ─────────────── Preset Rail ─────────────── */

  private renderPresetRail(filter = ''): void {
    const rail = el('#preset-rail');
    rail.innerHTML = '';
    const source = this.mode === 'generative' ? CURATED_PRESETS : ALL_PRESETS.slice(0, 200);
    const list = source.filter((p) => !filter || p.name.toLowerCase().includes(filter.toLowerCase()));
    for (const entry of list) {
      const card = document.createElement('button');
      card.className = 'preset-card';
      card.dataset.id = entry.id ?? entry.name;
      card.textContent = entry.name;
      card.onclick = () => this.selectPreset(entry as RhythmzPreset);
      rail.appendChild(card);
    }
    el('#preset-count').textContent = `${list.length}`;
  }

  private searchPresets(q: string): void { this.renderPresetRail(q); }

  private selectPreset(p: RhythmzPreset): void {
    const idx = this.engine.addPreset(p.name, p);
    this.engine.loadPreset(idx);
    this.highlightPreset(p.id ?? p.name);
  }

  private highlightPreset(id: string): void {
    document.querySelectorAll('.preset-card').forEach((c) => {
      c.classList.toggle('active', (c as HTMLElement).dataset.id === id);
    });
  }

  /* ─────────────── AI Generation ─────────────── */

  private async generateFromPrompt(prompt: string): Promise<void> {
    const text = prompt.trim();
    if (!text) { toast('Type a vibe first'); return; }
    const btn = el<HTMLButtonElement>('#ai-generate');
    btn.disabled = true;
    const orig = btn.textContent;
    btn.textContent = '✨ Generating…';

    // Update the AI overlay immediately so user sees activity
    const overlay = document.getElementById('ai-overlay');
    const overlayText = document.getElementById('ai-overlay-text');
    if (overlay && overlayText) {
      overlay.classList.add('active');
      overlayText.textContent = `🧠 Visual DJ AI: Generating preset for "${text.slice(0, 40)}..."`;
    }

    try {
      // Route through the coordinator so the agent panel + overlay update
      if (this.agentCoordinator) {
        await this.agentCoordinator.visualDJ.generatePreset(text);
        // Load the generated preset into the engine
        const result = await aiClient.generatePreset(text);
        this.loadAIPreset(result.preset);
        toast(result.meta?.ai ? `✨ ${result.meta.mood ?? 'AI'} preset generated` : 'AI unavailable — showing a preset');
      } else {
        // Fallback: direct call
        const result = await aiClient.generatePreset(text);
        this.loadAIPreset(result.preset);
        toast(result.meta?.ai ? `✨ ${result.meta.mood ?? 'AI'} preset generated` : 'AI unavailable — showing a preset');
      }
    } catch (e) {
      toast('AI generation failed — check gateway');
      console.error(e);
    } finally {
      btn.disabled = false;
      btn.textContent = orig;
      // Clear overlay after a delay
      if (overlay && overlayText) {
        setTimeout(() => {
          overlay.classList.remove('active');
          if (overlayText) overlayText.textContent = 'AI agents monitoring';
        }, 3000);
      }
    }
  }

  /* ─────────────── Audio Sources ─────────────── */

  private pickDemo(): void {
    const track = DEMO_TRACKS[Math.floor(Math.random() * DEMO_TRACKS.length)];
    this.audio.playDemo(track.url, track.title).catch(() => toast('Demo track missing'));
    this.setSourceActive('demo');
  }
  private pickFile(): void {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = 'audio/*';
    input.onchange = () => {
      const f = input.files?.[0];
      if (f) this.audio.playFile(f).then(() => this.setSourceActive('file')).catch(() => toast('Could not play file'));
    };
    input.click();
  }
  private useMic(): void {
    this.audio.useMic().then(() => this.setSourceActive('mic')).catch(() => toast('Microphone permission denied'));
  }
  private useDesktop(): void {
    this.audio.useDesktop().then(() => this.setSourceActive('desktop'))
      .catch((e) => toast(e instanceof Error ? e.message : 'Desktop audio failed'));
  }
  private useUrl(): void {
    const url = window.prompt('Audio URL (must allow CORS):');
    if (!url) return;
    this.audio.playUrl(url).then(() => this.setSourceActive('url'))
      .catch((e) => toast(e instanceof Error ? e.message : 'URL failed'));
  }
  private loadVideo(): void {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = 'video/*';
    input.onchange = async () => {
      const f = input.files?.[0];
      if (!f) return;
      try {
        await this.engine.loadVideo(f);
        this.setSourceActive('url');
        el<HTMLElement>('#src-video').classList.add('active');
        toast('🎬 Video layer loaded');
      } catch (e) {
        toast('Could not load video');
        console.error(e);
      }
    };
    input.click();
  }
  private setSourceActive(kind: SourceKind): void {
    document.querySelectorAll('.src-btn').forEach((b) => b.classList.remove('active'));
    el(`#src-${kind}`).classList.add('active');
  }
  private onSourceState(s: { label: string; playing: boolean }): void {
    el('#source-label').textContent = s.label;
    el('#play-indicator').classList.toggle('playing', s.playing);
  }

  /* ─────────────── Misc ─────────────── */

  private toggleFullscreen(): void {
    if (!document.fullscreenElement) document.documentElement.requestFullscreen?.();
    else document.exitFullscreen?.();
  }
  private onStats(s: { fps: number; presetName: string }): void {
    const fpsEl = document.getElementById('fps');
    if (fpsEl) fpsEl.textContent = s.fps ? `${s.fps}` : '—';
  }

  /* ─────────────── Glass-Box: Compute Pipeline ─────────────── */

  /** Wire GPU compute effects into the render loop. */
  private wireComputeEffects(): void {
    // Register effects that auto-dispatch each frame
    this.computePipeline.registerParticleCompute('starfield', 'Starfield Particles', 500000);
    this.computePipeline.registerFluidCompute('fluid-sim', 'Fluid Simulation', 128);
    this.computePipeline.registerCustomCompute('laser-grid', 'Laser Grid', 256, () => {});

    // Log initial VRAM state
    console.log(`[Glass Box] Compute pipeline: ${this.computePipeline.getAllEffects().length} effects registered`);
  }

  /* ─────────────── Glass-Box: Wire Slider to ControlMatrix ─────────────── */

  /* ─────────────── Admin ─────────────── */

  private checkAdminAccess(): void {
    const params = new URLSearchParams(location.search);
    const token = params.get('admin');
    if (token) { sessionStorage.setItem('rhythmz_admin', token); window.history.replaceState({}, '', location.pathname); }
    const stored = sessionStorage.getItem('rhythmz_admin');
    if (stored && stored === this.getAdminToken()) this.enableAdmin(stored);
  }
  private getAdminToken(): string {
    return (import.meta as unknown as { env?: { VITE_ADMIN_TOKEN?: string } }).env?.VITE_ADMIN_TOKEN || 'rhythmz-admin-local';
  }
  private enableAdmin(token: string): void {
    this.adminToken = token;
    el('body').classList.add('is-admin');
    el('#admin-panel').classList.add('open');
    this.exposeToolkit();
  }
  get isAdmin(): boolean { return this.adminToken !== null; }

  /* ─────────────── Glass-Box Telemetry ─────────────── */

/** Start streaming audio analysis data to the telemetry dashboard. */
  private startTelemetryStream(): void {
    this.telemetryInterval = window.setInterval(() => {
      const engine = this.engine as unknown as { analyzer?: { sample: (t: number) => { bass: number; lowMid: number; mid: number; highMid: number; treble: number; level: number; beat: number; bpm: number } } };
      const analyzer = engine.analyzer;
      if (!analyzer) return;

      const af = analyzer.sample(performance.now());
      const vram = this.computePipeline.getVRAMSnapshot();

      this.telemetry.updateTelemetry({
        audio: {
          subBass: af.bass,
          midBass: af.lowMid,
          lowMid: af.lowMid,
          mid: af.mid,
          highMid: af.highMid,
          treble: af.treble,
          rms: af.level,
          bpm: af.bpm,
          beat: af.beat > 0.3,
          beatConfidence: af.beat,
        },
        vramEstimate: {
          usedMB: vram.totalAllocatedMB,
          totalMB: vram.totalBudgetMB,
          particles: vram.effects.reduce((sum, e) => sum + (e.scale > 0 ? e.scale : 0), 0),
          objects: vram.effects.length,
        },
      });
    }, 250);
    // Store reference for potential cleanup
    if (this.telemetryInterval) void (this.telemetryInterval);
  }
}
